import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["align", "animate", "children", "onOpenStart", "onOpenComplete", "onCloseStart", "onCloseComplete", "onWheel", "panelKey", "rootNode", "scrollElementRef", "width", "maxWidth", "containerZIndex", "_dontMakeNewLayer", "panelStylesOverrides", "cssStyles"];
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { Provider as PanelComponentRegistrationProvider } from '../../context/PanelComponentRegistrationContext';
import * as PanelContext from '../../context/PanelContext';
import { constrainVerticalWheelEvent } from '../../utils/EventHandlers';
import { ShareScrollElement } from '../ScrollingColumn/ShareScrollElement';
import { Layer } from '../Layer/Layer';
import { percentToVW } from '../../utils/styles';
import { PanelTransition } from '../PanelTransition/PanelTransition';
import styled from 'foundations-theming/utils';
import { StyledForm } from '../../../components/Form/Form';
import { rgba } from '../../utils/color';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { usePanelPanelNavigatorContext } from '../PanelNavigator/PanelNavigatorProvider';
import { getPanelTransitionProps } from '../PanelNavigator/panelUtils';
import { withTokenProps } from '../../../utils/withTokenProps';
import { JUPITER_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
const inPanelContext = {
  inPanel: true
};
const StyledPanel = styled.div.withConfig({
  displayName: "Panel__StyledPanel",
  componentId: "y1w4hw-0"
})(({
  theme,
  $isSandboxed,
  $align,
  $maxWidth,
  $width,
  $panelStylesOverrides = {},
  $cssStyles
}) => {
  return deepMerge(Object.assign({
    display: 'inline-flex',
    flexDirection: 'row',
    background: theme.color['fill-surface-default'],
    height: '100%',
    maxWidth: $maxWidth,
    minWidth: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
    width: $width,
    zIndex: JUPITER_LAYER
  }, $align === 'left' && {
    boxShadow: `3px 0 8px 0 ${rgba(theme.color['fill-accent-neutral-default'], 0.21)}`,
    left: 0
  }, $align === 'right' && {
    boxShadow: `-3px 0 8px 0 ${rgba(theme.color['fill-accent-neutral-default'], 0.21)}`,
    right: 0
  }, !!$isSandboxed && {
    bottom: 0,
    position: 'absolute',
    top: 0
  }, $cssStyles), $panelStylesOverrides);
});
const StyledPanelContainer = styled.div.withConfig({
  displayName: "Panel__StyledPanelContainer",
  componentId: "y1w4hw-1"
})(["display:flex;flex-direction:column;min-height:100%;width:100%;> ", "{display:flex;flex:1 1 100%;flex-direction:column;max-height:100%;}"], StyledForm);
class PanelComponent extends Component {
  constructor(props) {
    super(props);
    this.handleWheel = evt => {
      // Allow a custom wheel handler to override this one
      const {
        onWheel,
        scrollElementRef
      } = this.props;
      if (typeof onWheel === 'function') {
        onWheel(evt);
        return;
      }
      constrainVerticalWheelEvent(evt, scrollElementRef.current);
    };
    this.state = {
      bodyCount: 0,
      headerCount: 0
    };
    this._getTransitionProps = memoizeOne(getPanelTransitionProps);
    this._isMounted = true; // Not technically, but child context funcs are called before didMount.
    // Track the number of PanelHeader and PanelBody instances this panel contains. This code
    // should be removed once all headerless/bodyless panels are eliminated.
    this._panelComponentRegistrationContextValue = {
      registerPanelBody: () => {
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount + 1
          }));
        }
      },
      unregisterPanelBody: () => {
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount - 1
          }));
        }
      },
      registerPanelHeader: () => {
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount + 1
          }));
        }
      },
      unregisterPanelHeader: () => {
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount - 1
          }));
        }
      }
    };
  }
  componentDidMount() {
    const {
      scrollElementRef
    } = this.props;

    // Only apply focus if the panel is not sandboxed in UI Library.
    if ((!this.context).sandboxed) {
      scrollElementRef.current.focus(); // adding focus so users can tab into panel
    }

    // Using the React `onWheel` event would be restrictive in Chrome (#6272)
    scrollElementRef.current.addEventListener('wheel', this.handleWheel);
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  render() {
    const _this$props = this.props,
      {
        align = 'right',
        animate = true,
        children,
        onOpenStart,
        onOpenComplete,
        onCloseStart,
        onCloseComplete,
        rootNode,
        scrollElementRef,
        width = '30%',
        maxWidth = '100%',
        containerZIndex = '1111',
        _dontMakeNewLayer = false,
        panelStylesOverrides,
        cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      sandboxed
    } = this.context;
    const computedWidth = percentToVW(width);
    const computedMaxWidth = percentToVW(maxWidth);
    const renderedPanel = /*#__PURE__*/_jsx(PanelContext.Provider, {
      value: inPanelContext,
      children: /*#__PURE__*/_jsx(StyledPanel, Object.assign({
        ref: scrollElementRef,
        tabIndex: 0,
        $isSandboxed: sandboxed,
        $align: align,
        $width: computedWidth,
        $maxWidth: computedMaxWidth,
        $panelStylesOverrides: panelStylesOverrides,
        $cssStyles: cssStyles
      }, rest, {
        children: /*#__PURE__*/_jsx(PanelComponentRegistrationProvider, {
          value: this._panelComponentRegistrationContextValue,
          children: /*#__PURE__*/_jsx(StyledPanelContainer, {
            children: children
          })
        })
      }))
    });
    if (_dontMakeNewLayer || sandboxed) {
      return renderedPanel;
    }
    return /*#__PURE__*/_jsx(Layer, {
      "data-layer-for": "Panel",
      rootNode: rootNode,
      Transition: PanelTransition,
      transitionProps: this._getTransitionProps(onOpenStart, onOpenComplete, onCloseStart, onCloseComplete, align, animate, containerZIndex),
      children: renderedPanel
    });
  }
}
const PanelWrapper = props => {
  var _panelPanelNavigatorC;
  const panelPanelNavigatorContext = usePanelPanelNavigatorContext();
  const panelStylesOverrides = (_panelPanelNavigatorC = panelPanelNavigatorContext === null || panelPanelNavigatorContext === void 0 ? void 0 : panelPanelNavigatorContext.panelStylesOverrides) !== null && _panelPanelNavigatorC !== void 0 ? _panelPanelNavigatorC : {};
  return /*#__PURE__*/_jsx(PanelComponent, Object.assign({}, props, {
    panelStylesOverrides: panelStylesOverrides
  }));
};
export const Panel = ShareScrollElement(withTokenProps(PanelWrapper));